<script setup>
import { Cropper } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
import { ref, computed, nextTick, onMounted } from "vue";
import {
  useFormField,
  usePasswordField,
  usePasswordConfirmField,
  useFormValidation,
  useFormChanges,
} from "~/composables/useForm";
import { useAuth } from "~/composables/useAuth";
import { useProfile } from "~/composables/useProfile";

definePageMeta({
  layout: "mypage",
});

// ===== Auth & Profile =====
const { currentUser } = useAuth();
const { updateProfile } = useProfile();

// ===== 사용자 정보 =====
const userNickname = computed(() => currentUser.value?.nickname || "");
const userId = computed(() => currentUser.value?.id || "");
const userAvatar = computed(() => currentUser.value?.avatar || "/images/default_avatar_01.png");

// ===== 초기값 =====
const initialValues = ref({
  nickname: "",
  password: "",
  avatar: "/images/default_avatar_01.png",
});

// ===== 폼 필드 =====
// 닉네임 필드
const nickname = useFormField("", {
  required: true,
  errorMessages: {
    empty: "닉네임을 입력해주세요.",
  },
});

// Load user data on mount
onMounted(() => {
  if (currentUser.value) {
    nickname.value.value = currentUser.value.nickname || "";
    currentAvatar.value = currentUser.value.avatar || "/images/default_avatar_01.png";
    initialValues.value.nickname = currentUser.value.nickname || "";
    initialValues.value.avatar = currentUser.value.avatar || "/images/default_avatar_01.png";
  }
});

// 비밀번호 필드
const password = usePasswordField("", {
  required: false,
  errorMessages: {
    empty: "비밀번호를 입력해주세요.",
    invalid: "비밀번호는 영문숫자 포함 8자 이상이어야 합니다.",
  },
});

// 비밀번호 확인 필드
const passwordConfirm = usePasswordConfirmField("", password, {
  errorMessages: {
    empty: "비밀번호를 입력해주세요.",
    mismatch: "비밀번호가 일치하지 않습니다.",
  },
});

// ===== 아바타 관련 상태 =====
// 프로필 편집 모달 ref
const editProfileModal = ref(null);
// 현재 설정된 아바타
const currentAvatar = ref("/images/default_avatar_01.png");
// 모달에서 선택한 아바타 (미리보기용)
const selectedAvatar = ref("/images/default_avatar_01.png");
// 업로드한 이미지 파일
const uploadedImageFile = ref(null);
// 크롭할 이미지 소스
const cropImageSrc = ref(null);
// 크롭퍼 컴포넌트 ref
const cropperRef = ref(null);
// 기본 아바타 목록
const defaultAvatars = [
  "/images/default_avatar_01.png",
  "/images/default_avatar_02.png",
  "/images/default_avatar_03.png",
  "/images/default_avatar_04.png",
  "/images/default_avatar_05.png",
  "/images/default_avatar_06.png",
];

// ===== 폼 관리 =====
const form = useFormValidation(
  {
    nickname,
    password,
    passwordConfirm,
  },
  {
    onSubmit: async (values) => {
      const profileData = {
        nickname: values.nickname,
        avatar: currentAvatar.value
      };

      // 비밀번호가 입력된 경우만 추가
      if (values.password) {
        profileData.password = values.password;
      }

      const result = await updateProfile(profileData);

      if (!result.success) {
        alert(result.message || '프로필 업데이트에 실패했습니다.');
        return false;
      }

      // 성공 시 사용자 정보 새로고침 (모든 컴포넌트에 즉시 반영)
      await refreshUser();

      // 초기값 업데이트
      initialValues.value.nickname = values.nickname;
      initialValues.value.password = "";
      initialValues.value.avatar = currentAvatar.value;

      // 비밀번호 필드 초기화
      password.value.value = "";
      passwordConfirm.value.value = "";

      alert('프로필이 업데이트되었습니다.');
      return true;
    },
  }
);

// 변경 사항 추적
const changes = useFormChanges(
  { nickname, password },
  computed(() => ({
    nickname: initialValues.value.nickname,
    password: initialValues.value.password,
  }))
);

// 아바타 변경 여부
const hasAvatarChanged = computed(() => {
  return currentAvatar.value !== initialValues.value.avatar;
});

// 저장 버튼 비활성화 여부
const isSaveDisabled = computed(() => {
  // 에러가 있으면 비활성화
  if (
    nickname.isError.value ||
    password.isError.value ||
    passwordConfirm.isError.value
  ) {
    return true;
  }

  // 닉네임이 비어있으면 비활성화
  if (!nickname.value.value.trim()) {
    return true;
  }

  // 비밀번호가 입력되었는데 유효하지 않으면 비활성화
  if (password.value.value && !password.validate()) {
    return true;
  }

  // 비밀번호 확인이 일치하지 않으면 비활성화
  if (password.value.value && !passwordConfirm.validateMatch()) {
    return true;
  }

  // 변경사항이 없으면 비활성화
  if (!changes.hasAnyChanges.value && !hasAvatarChanged.value) {
    return true;
  }

  return false;
});

// ===== 아바타 관련 함수 =====
// 선택된 아바타 미리보기 이미지
const previewAvatar = computed(() => {
  const isDefaultAvatar = defaultAvatars.includes(selectedAvatar.value);
  if (isDefaultAvatar) {
    const match = selectedAvatar.value.match(/default_avatar_(\d+)\.png/);
    if (match) {
      const number = match[1];
      return `/images/default_avatar_preview_${number}.png`;
    }
  }
  return selectedAvatar.value;
});

// 아바타 선택 핸들러
const selectAvatar = (avatarPath) => {
  selectedAvatar.value = avatarPath;
  cropImageSrc.value = null;
  uploadedImageFile.value = null;
};

// 파일 선택 핸들러
const handleFileSelect = (event) => {
  const file = event.target.files?.[0];
  if (!file) return;

  if (!file.type.startsWith("image/")) {
    alert("이미지 파일만 선택 가능합니다.");
    return;
  }

  const reader = new FileReader();
  reader.onload = (e) => {
    cropImageSrc.value = e.target.result;
    uploadedImageFile.value = file;
    selectedAvatar.value = null;
  };
  reader.readAsDataURL(file);

  event.target.value = "";
};

// 크롭 결과 변경 핸들러
const handleCropChange = ({ canvas }) => {
  if (canvas) {
    const croppedCanvas = document.createElement("canvas");
    croppedCanvas.width = 256;
    croppedCanvas.height = 256;
    const ctx = croppedCanvas.getContext("2d");
    ctx.drawImage(canvas, 0, 0, 256, 256);
    selectedAvatar.value = croppedCanvas.toDataURL("image/png");
  }
};

// 크롭퍼 준비 완료 핸들러
const handleCropReady = () => {
  if (cropperRef.value) {
    nextTick(() => {
      const result = cropperRef.value?.getResult();
      if (result && result.canvas) {
        const croppedCanvas = document.createElement("canvas");
        croppedCanvas.width = 256;
        croppedCanvas.height = 256;
        const ctx = croppedCanvas.getContext("2d");
        ctx.drawImage(result.canvas, 0, 0, 256, 256);
        selectedAvatar.value = croppedCanvas.toDataURL("image/png");
      }
    });
  }
};

// 프로필 편집 모달 열기
const editProfile = () => {
  selectedAvatar.value = currentAvatar.value;
  uploadedImageFile.value = null;
  cropImageSrc.value = null;
  editProfileModal.value?.open();
};

// 아바타 저장 핸들러
const saveAvatar = (close) => {
  currentAvatar.value = selectedAvatar.value;
  close();
};

// ===== 폼 제출 =====
const handleSave = async () => {
  await form.handleSubmit();
};
</script>

<template>
  <div class="mypage__group">
    <div class="mypage__title-sub">
      <h3>프로필 설정</h3>
    </div>
    <div class="mypage__content">
      <!-- ===== 프로필 이미지 영역 ===== -->
      <div class="mypage__thumb">
        <div class="mypage__thumb-profile">
          <img :src="currentAvatar" alt="" />
          <button
            type="button"
            class="mypage__thumb-edit ico-before ico-photo"
            @click="editProfile"
          >
            <span class="blind">프로필 변경</span>
          </button>
        </div>
        <div class="mypage__thumb-name">
          <strong class="mypage__thumb-name-title">{{ userNickname }}</strong>
          <p class="mypage__thumb-name-id">
            <span>ID:</span>
            <span>{{ userId }}</span>
          </p>
        </div>
      </div>

      <!-- ===== 입력 폼 영역 ===== -->
      <div class="mypage__info">
        <!-- 닉네임 -->
        <div class="mypage__info-item">
          <label for="nickname" class="mypage__info-label">닉네임</label>
          <input
            ref="nickname.inputRef"
            type="text"
            class="input-text"
            :class="{
              'input-error': nickname.isError.value,
              'input-shake': nickname.shakeKey.value > 0,
            }"
            :key="`nickname-${nickname.shakeKey.value}`"
            id="nickname"
            placeholder="닉네임을 입력해주세요."
            v-model="nickname.value.value"
            @blur="nickname.handleBlur"
          />
          <Transition name="error">
            <span v-if="nickname.isError.value" class="mypage__info-error">
              {{ nickname.errorMessage.value }}
            </span>
          </Transition>
        </div>
        <!-- ID (읽기 전용) -->
        <div class="mypage__info-item">
          <label for="userid" class="mypage__info-label">ID</label>
          <input
            type="text"
            class="input-text"
            id="userid"
            :value="userId"
            readonly
          />
        </div>
        <!-- 비밀번호 변경 -->
        <div class="mypage__info-item">
          <label for="password" class="mypage__info-label">비밀번호 변경</label>
          <div class="mypage__info-eye-wrap">
            <input
              ref="password.inputRef"
              :type="password.showPassword.value ? 'text' : 'password'"
              class="input-text input-pw"
              :class="{
                'input-error': password.isError.value,
                'input-shake': password.shakeKey.value > 0,
              }"
              :key="`password-${password.shakeKey.value}`"
              id="password"
              placeholder="비밀번호를 입력해주세요."
              v-model="password.value.value"
              @focus="password.handleFocus"
              @blur="password.handleBlur"
            />
            <button
              v-show="password.value.value.length > 0"
              type="button"
              class="mypage__info-eye"
              :class="
                password.showPassword.value ? 'ico-eye-active' : 'ico-eye'
              "
              @click="password.togglePassword"
            >
              <span class="blind">비밀번호 보기</span>
            </button>
          </div>
          <Transition name="error">
            <span v-if="password.isError.value" class="mypage__info-error">
              {{ password.errorMessage.value }}
            </span>
          </Transition>
          <!-- 비밀번호 확인 -->
          <div class="mypage__info-eye-wrap">
            <input
              ref="passwordConfirm.inputRef"
              :type="passwordConfirm.showPassword.value ? 'text' : 'password'"
              class="input-text input-pw"
              :class="{
                'input-error': passwordConfirm.isError.value,
                'input-shake': passwordConfirm.shakeKey.value > 0,
              }"
              :key="`password-confirm-${passwordConfirm.shakeKey.value}`"
              id="password-confirm"
              placeholder="새로운 비밀번호 확인"
              v-model="passwordConfirm.value.value"
              @focus="passwordConfirm.handleFocus"
              @blur="passwordConfirm.handleBlur"
            />
            <button
              v-show="passwordConfirm.value.value.length > 0"
              type="button"
              class="mypage__info-eye"
              :class="
                passwordConfirm.showPassword.value
                  ? 'ico-eye-active'
                  : 'ico-eye'
              "
              @click="passwordConfirm.togglePassword"
            >
              <span class="blind">비밀번호 보기</span>
            </button>
          </div>
          <Transition name="error">
            <span
              v-if="passwordConfirm.isError.value"
              class="mypage__info-error"
            >
              {{ passwordConfirm.errorMessage.value }}
            </span>
          </Transition>
        </div>
        <!-- 저장 버튼 -->
        <div class="mypage__btn">
          <button
            class="btn-primary btn-md-plus"
            :disabled="isSaveDisabled"
            @click="handleSave"
          >
            저장
          </button>
        </div>
      </div>
    </div>
  </div>
  <!-- ===== 프로필 편집 모달 ===== -->
  <Modal ref="editProfileModal" title="내 프로필 편집하기" type="d">
    <template #default="{ close }">
      <div class="modal__content-body">
        <div class="modal__edit-profile">
          <!-- 이미지 크롭 영역 -->
          <div class="modal__edit-profile-crop">
            <div class="modal__edit-profile-thumb">
              <Cropper
                v-if="cropImageSrc"
                ref="cropperRef"
                :src="cropImageSrc"
                :stencil-props="{
                  aspectRatio: 1,
                }"
                :stencil-size="{
                  width: 256,
                  height: 256,
                }"
                :auto-zoom="true"
                :transitions="false"
                class="modal__edit-profile-cropper modal__edit-profile-cropper--hidden"
                @change="handleCropChange"
                @ready="handleCropReady"
              />
              <img :src="previewAvatar" alt="" />
              <label
                for="profile-crop"
                class="modal__edit-profile-crop-label ico-before ico-photo"
                role="button"
              >
                <span class="blind">프로필 변경</span>
                <input
                  type="file"
                  class="blind"
                  id="profile-crop"
                  accept="image/*"
                  @change="handleFileSelect"
                />
              </label>
            </div>
          </div>
          <!-- 기본 아바타 선택 영역 -->
          <div class="modal__edit-profile-avatar">
            <strong class="modal__edit-profile-avatar-title">기본아바타</strong>
            <div class="modal__edit-profile-avatar-list">
              <div
                v-for="(avatar, index) in defaultAvatars"
                :key="index"
                class="modal__edit-profile-avatar-item"
                :class="{
                  'modal__edit-profile-avatar-item--active':
                    selectedAvatar === avatar,
                }"
                role="button"
                @click="selectAvatar(avatar)"
              >
                <img :src="avatar" alt="" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal__content-footer modal__content-footer--start">
        <button
          type="button"
          class="btn-gray btn-block btn-md-plus"
          @click="close"
        >
          취소
        </button>
        <button
          type="button"
          class="btn-primary btn-block btn-md-plus"
          @click="saveAvatar(close)"
        >
          저장
        </button>
      </div>
    </template>
  </Modal>
</template>

<style scoped>
.mypage__info-error {
  overflow: hidden;
}

.modal__edit-profile-cropper {
  width: 100%;
  height: 100%;
  max-width: 256px;
  max-height: 256px;
  margin: 0 auto;
}

.modal__edit-profile-cropper--hidden {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  z-index: -1;
  width: 256px;
  height: 256px;
}
</style>
